import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { ChatCircleDots, X, PaperPlaneRight, Robot, User } from "@phosphor-icons/react";

const SID_KEY = "fixigo_chat_sid";
const getSid = () => {
  let s = localStorage.getItem(SID_KEY);
  if (!s) { s = "web-" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(SID_KEY, s); }
  return s;
};

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hi! I'm Fixi 🤖 from Fixigo. How can I help — booking a service, prices, or something else?" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const endRef = useRef(null);
  const sid = useRef(getSid());

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: t }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/message", { session_id: sid.current, message: t });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.action === "handoff") setHandoff(true);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble right now. Please try again, or WhatsApp us at +91 91529 43297." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-widget-open"
          className="fixed bottom-24 right-5 z-40 h-14 w-14 rounded-full bg-[#4F46E5] text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform">
          <ChatCircleDots size={26} weight="fill" />
          <span className="absolute -top-1 -right-1 h-4 w-4 bg-green-500 rounded-full border-2 border-white" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-[380px] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200" data-testid="chat-widget">
          <div className="bg-[#4F46E5] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center"><Robot size={20} weight="fill" /></div>
              <div><p className="font-bold text-sm leading-tight">Fixi · Fixigo Assistant</p><p className="text-[11px] text-white/80">Typically replies instantly</p></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close" className="hover:bg-white/20 rounded-full p-1"><X size={20} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#f8fafc]" data-testid="chat-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && <div className="h-7 w-7 rounded-full bg-[#eef2ff] flex items-center justify-center shrink-0"><Robot size={15} className="text-[#4F46E5]" /></div>}
                <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-[#4F46E5] text-white rounded-br-sm" : "bg-white border border-gray-200 rounded-bl-sm"}`}>{m.text}</div>
                {m.role === "user" && <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0"><User size={15} /></div>}
              </div>
            ))}
            {busy && <div className="flex gap-2"><div className="h-7 w-7 rounded-full bg-[#eef2ff] flex items-center justify-center"><Robot size={15} className="text-[#4F46E5]" /></div><div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-muted-foreground">typing…</div></div>}
            {handoff && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs text-amber-800 text-center">
                Our team will reach out shortly. For urgent help, <a href="https://wa.me/919152943297" target="_blank" rel="noreferrer" className="font-semibold underline">WhatsApp us</a>.
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-2 border-t border-gray-100 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your message…" disabled={busy} data-testid="chat-input"
              className="flex-1 rounded-full border border-gray-200 px-4 text-sm outline-none focus:border-[#4F46E5]" />
            <button onClick={() => send()} disabled={busy} data-testid="chat-send"
              className="h-10 w-10 rounded-full bg-[#4F46E5] text-white flex items-center justify-center disabled:opacity-50 shrink-0"><PaperPlaneRight size={18} weight="fill" /></button>
          </div>
        </div>
      )}
    </>
  );
}
